import React, { useEffect, useRef, useState } from "react";
import { Accessibility, X, Plus, Minus, Contrast, Underline, Eye, RotateCcw } from "lucide-react";
import { useLang } from "../../lib/i18n";

const STORAGE_KEY = "pd_a11y_prefs";
const DEFAULTS = { fontScale: 0, contrast: false, underline: false, reduceMotion: false };

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { ...DEFAULTS };
}

// Apply preferences to <html> via classes + a font-scale CSS variable.
function applyPrefs(p) {
  const root = document.documentElement;
  root.style.setProperty("--a11y-font-scale", String(1 + p.fontScale * 0.1));
  root.classList.toggle("a11y-contrast", p.contrast);
  root.classList.toggle("a11y-underline", p.underline);
  root.classList.toggle("a11y-reduce-motion", p.reduceMotion);
}

const ToggleRow = ({ icon: Icon, label, active, onClick, testid }) => {
  const { t } = useLang();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testid}
      className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border transition-colors text-left ${
        active ? "bg-navy text-white border-navy" : "bg-surface border-outline/50 text-ink hover:border-navy/40"
      }`}
    >
      <span className="flex items-center gap-3 text-[14px] font-medium">
        <Icon className="h-4 w-4 shrink-0" aria-hidden /> {label}
      </span>
      <span className={`text-[11px] font-bold uppercase ${active ? "text-white/90" : "text-ink-muted"}`}>
        {active ? t("a11yOn") : t("a11yOff")}
      </span>
    </button>
  );
};

export default function AccessibilityWidget() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);
  const panelRef = useRef(null);

  useEffect(() => {
    applyPrefs(prefs);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* ignore */
    }
  }, [prefs]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const update = (patch) => setPrefs((p) => ({ ...p, ...patch }));
  const setFont = (delta) => setPrefs((p) => ({ ...p, fontScale: Math.max(0, Math.min(4, p.fontScale + delta)) }));


  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("a11yOpen")}
        aria-expanded={open}
        data-testid="a11y-toggle"
        className="fixed left-3 bottom-24 md:bottom-6 z-50 h-12 w-12 rounded-full bg-navy text-white shadow-[0_8px_30px_rgba(0,51,102,0.35)] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform focus:outline-none focus-visible:ring-4 focus-visible:ring-navy/40"
      >
        <Accessibility className="h-6 w-6" aria-hidden />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-label="Barrierefreiheit"
          data-testid="a11y-panel"
          className="fixed left-3 bottom-40 md:bottom-20 z-50 w-[300px] max-w-[calc(100vw-1.5rem)] rounded-2xl bg-surface neu-card !shadow-2xl border border-outline/40 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-navy-dark flex items-center gap-2">
              <Accessibility className="h-5 w-5 text-brandred" aria-hidden /> {t("a11yTitle")}
            </h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("a11yClose")}
              data-testid="a11y-close"
              className="p-1.5 rounded-lg text-ink-muted hover:bg-surface-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mb-4">
            <div className="text-[13px] font-semibold text-navy-dark mb-2">{t("a11yFontSize")}</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFont(-1)}
                aria-label={t("a11yFontMinus")}
                data-testid="a11y-font-minus"
                className="h-10 w-10 rounded-xl border border-outline/50 bg-surface flex items-center justify-center text-navy hover:border-navy/40 disabled:opacity-40"
                disabled={prefs.fontScale === 0}
              >
                <Minus className="h-4 w-4" />
              </button>
              <div className="flex-1 text-center text-sm font-semibold text-ink" data-testid="a11y-font-level">
                {Math.round((1 + prefs.fontScale * 0.1) * 100)}%
              </div>
              <button
                type="button"
                onClick={() => setFont(1)}
                aria-label={t("a11yFontPlus")}
                data-testid="a11y-font-plus"
                className="h-10 w-10 rounded-xl border border-outline/50 bg-surface flex items-center justify-center text-navy hover:border-navy/40 disabled:opacity-40"
                disabled={prefs.fontScale === 4}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="space-y-2.5">
            <ToggleRow icon={Contrast} label={t("a11yContrast")} active={prefs.contrast} onClick={() => update({ contrast: !prefs.contrast })} testid="a11y-contrast" />
            <ToggleRow icon={Underline} label={t("a11yUnderline")} active={prefs.underline} onClick={() => update({ underline: !prefs.underline })} testid="a11y-underline" />
            <ToggleRow icon={Eye} label={t("a11yReduceMotion")} active={prefs.reduceMotion} onClick={() => update({ reduceMotion: !prefs.reduceMotion })} testid="a11y-reduce-motion" />
          </div>

          <button
            type="button"
            onClick={() => setPrefs({ ...DEFAULTS })}
            data-testid="a11y-reset"
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold text-brandred border border-brandred/30 hover:bg-brandred/5 transition-colors"
          >
            <RotateCcw className="h-4 w-4" /> {t("a11yReset")}
          </button>
        </div>
      )}
    </>
  );
}
